import { ApolloClient } from "@apollo/client";
import {
  IAnalyticsRepository,
  IAnalyticsRepositoryParams,
  IAnalyticsRepositoryResponse,
  IAnalyticsScoreCardsRepositoryResponse,
  IDeductionsByPurchaserRepositoryResponse,
  IDeductionsByTypeAndReasonRepositoryResponse,
  IDeductionVolumeVsGrossSalesRepositoryResponse,
  IDeductionsByAgingBracketsRepositoryResponse,
  IDisputePipelineRepositoryResponse,
  IDeductionsByRetailerRepositoryResponse,
  ICreateAnalyticsShareLinkInput,
  ICreateAnalyticsShareLinkResult,
  IAnalyticsShareLink,
} from "@/core/repositories/analytics";
import {
  GET_UNRESOLVED_DEDUCTIONS_ANALYTICS,
  GET_ANALYTICS_DEDUCTIONS_BY_PURCHASER,
  GET_ANALYTICS_DEDUCTIONS_BY_TYPE_AND_REASON,
  GET_ANALYTICS_DEDUCTION_VOLUME_VS_GROSS_SALES,
  GET_ANALYTICS_DEDUCTIONS_BY_AGING_BRACKETS,
  GET_ANALYTICS_DISPUTE_PIPELINE,
  GET_ANALYTICS_DEDUCTIONS_BY_RETAILER,
} from "@/core/graphql/analytics";
import { ApplicationError } from "@/infrastructure/errors/ApplicationError";
import { GET_ANALYTICS_SCORE_CARDS } from "@/core/graphql/analytics/getAnalyticsScoreCards";
import { CREATE_ANALYTICS_SHARE_LINK } from "@/core/graphql/analytics/createAnalyticsShareLink";
import { GET_ANALYTICS_SHARE_LINK } from "@/core/graphql/analytics/getAnalyticsShareLink";
import { splitMixedRowsIntoGroups, toTypeAndReasonGroups } from "@/core/utils/filterDeductionRowsByDimension";

interface IQueryResponse {
  unresolvedDeductionsAnalytics: IAnalyticsRepositoryResponse;
  scoreCardsAnalytics: IAnalyticsScoreCardsRepositoryResponse;
  analyticsDeductionsByPurchaser: IDeductionsByPurchaserRepositoryResponse;
  analyticsDeductionsByTypeAndReason: IDeductionsByTypeAndReasonRepositoryResponse;
  analyticsDeductionVolumeVsGrossSales: IDeductionVolumeVsGrossSalesRepositoryResponse;
  analyticsDeductionsByAgingBrackets: IDeductionsByAgingBracketsRepositoryResponse;
  analyticsDisputePipeline: IDisputePipelineRepositoryResponse;
  analyticsDeductionsByRetailer: IDeductionsByRetailerRepositoryResponse;
  createAnalyticsShareLink: ICreateAnalyticsShareLinkResult;
  getAnalyticsShareLink: IAnalyticsShareLink;
}

export class AnalyticsRepository implements IAnalyticsRepository {
  constructor(private readonly client: ApolloClient) { }

  async getUnresolvedDeductionsAnalytics(
    params: IAnalyticsRepositoryParams
  ): Promise<IAnalyticsRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_UNRESOLVED_DEDUCTIONS_ANALYTICS,
      variables: { startDate: params.startDate, endDate: params.endDate, purchaserIds: params.purchaserIds },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.unresolvedDeductionsAnalytics) throw new ApplicationError("Failed to get unresolved deductions");

    return data.unresolvedDeductionsAnalytics;
  }

  async getAnalyticsScoreCards(
    params: IAnalyticsRepositoryParams
  ): Promise<IAnalyticsScoreCardsRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_SCORE_CARDS,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.scoreCardsAnalytics) throw new ApplicationError("Failed to get score cards");

    return data.scoreCardsAnalytics;
  }

  async getDeductionsByPurchaser(
    params: IAnalyticsRepositoryParams
  ): Promise<IDeductionsByPurchaserRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_DEDUCTIONS_BY_PURCHASER,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.analyticsDeductionsByPurchaser) throw new ApplicationError("Failed to get deductions by purchaser");

    return data.analyticsDeductionsByPurchaser;
  }

  async getDeductionsByTypeAndReason(
    params: IAnalyticsRepositoryParams
  ): Promise<IDeductionsByTypeAndReasonRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_DEDUCTIONS_BY_TYPE_AND_REASON,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.analyticsDeductionsByTypeAndReason) {
      throw new ApplicationError("Failed to get deductions by type and reason");
    }

    return splitMixedRowsIntoGroups(toTypeAndReasonGroups(data.analyticsDeductionsByTypeAndReason));
  }

  async getDeductionVolumeVsGrossSales(
    params: IAnalyticsRepositoryParams
  ): Promise<IDeductionVolumeVsGrossSalesRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_DEDUCTION_VOLUME_VS_GROSS_SALES,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.analyticsDeductionVolumeVsGrossSales) {
      throw new ApplicationError("Failed to get deduction volume vs gross sales");
    }

    return data.analyticsDeductionVolumeVsGrossSales;
  }

  async getDeductionsByAgingBrackets(
    params: IAnalyticsRepositoryParams
  ): Promise<IDeductionsByAgingBracketsRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_DEDUCTIONS_BY_AGING_BRACKETS,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.analyticsDeductionsByAgingBrackets) {
      throw new ApplicationError("Failed to get deductions by aging brackets");
    }

    return data.analyticsDeductionsByAgingBrackets;
  }

  async getDisputePipeline(params: IAnalyticsRepositoryParams): Promise<IDisputePipelineRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_DISPUTE_PIPELINE,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.analyticsDisputePipeline) {
      throw new ApplicationError("Failed to get dispute pipeline");
    }

    return data.analyticsDisputePipeline;
  }

  async getDeductionsByRetailer(
    params: IAnalyticsRepositoryParams
  ): Promise<IDeductionsByRetailerRepositoryResponse> {
    const { data } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_DEDUCTIONS_BY_RETAILER,
      variables: { params },
      fetchPolicy: "network-only",
    });

    if (!data) throw new ApplicationError("Failed to load");
    if (!data?.analyticsDeductionsByRetailer) {
      throw new ApplicationError("Failed to get deductions by retailer");
    }

    return data.analyticsDeductionsByRetailer;
  }

  async createAnalyticsShareLink(
    input: ICreateAnalyticsShareLinkInput
  ): Promise<ICreateAnalyticsShareLinkResult> {
    const { data } = await this.client.mutate<IQueryResponse>({
      mutation: CREATE_ANALYTICS_SHARE_LINK,
      variables: { input },
    });

    if (!data?.createAnalyticsShareLink) {
      throw new ApplicationError("Failed to create share link");
    }

    return data.createAnalyticsShareLink;
  }

  async getAnalyticsShareLink(): Promise<IAnalyticsShareLink> {
    const { data, error } = await this.client.query<IQueryResponse>({
      query: GET_ANALYTICS_SHARE_LINK,
      fetchPolicy: "network-only",
    });

    if (error) {
      const message = error.message ?? "";
      if (message.includes("SHARE_LINK_EXPIRED")) {
        throw new ApplicationError("SHARE_LINK_EXPIRED");
      }
      throw new ApplicationError(message || "Failed to load share link");
    }

    if (!data?.getAnalyticsShareLink) {
      throw new ApplicationError("Failed to load share link");
    }

    const link = data.getAnalyticsShareLink;
    return {
      resourceType: link.resourceType as IAnalyticsShareLink["resourceType"],
      resourceSlug: link.resourceSlug,
      resourcePayload: link.resourcePayload as IAnalyticsShareLink["resourcePayload"],
      brand: {
        brandKey: link.brand.brandKey,
        brandName: link.brand.brandName,
        brandLogoUrl: link.brand.brandLogoUrl ?? null,
      },
      expiresAt: link.expiresAt,
      isExpired: link.isExpired,
    };
  }
}
